import React from 'react';

// AC-963 / S6.1 — drag-and-drop foundation for the layout builder.
//
// Native HTML5 DnD (same approach the footer-links editor uses) — no external lib. A custom mime type
// marks our drags so foreign drops are ignored. The dataTransfer payload is only readable on `drop`
// (it's protected during `dragover`), so the live drag descriptor is also held in component state for
// drawing insertion indicators; dataTransfer carries the same payload as the source of truth on drop.

export const SF_LAYOUT_DND_MIME = 'application/x-sf-layout';

export type DragKind = 'row' | 'column' | 'element' | 'new-element';

export interface IDragPayload {
    kind: DragKind;
    rowIndex?: number;
    colIndex?: number;
    elIndex?: number;
    elementType?: string; // for kind 'new-element'
}

export function setDragPayload(e: React.DragEvent, payload: IDragPayload): void {
    e.dataTransfer.setData(SF_LAYOUT_DND_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
}

export function getDragPayload(e: React.DragEvent): IDragPayload | null {
    try {
        const raw = e.dataTransfer.getData(SF_LAYOUT_DND_MIME);
        return raw ? (JSON.parse(raw) as IDragPayload) : null;
    } catch {
        return null;
    }
}

// True when the current drag is one of ours (checkable during dragover, where getData is blocked).
export function isLayoutDrag(e: React.DragEvent): boolean {
    return Array.from(e.dataTransfer.types).includes(SF_LAYOUT_DND_MIME);
}

// Which side of an element the pointer is on — used to decide insert-before vs insert-after and to
// place the insertion indicator. `vertical` compares Y (rows / stacked elements), else X (columns).
export function dropAfter(e: React.DragEvent, target: HTMLElement, vertical: boolean): boolean {
    const rect = target.getBoundingClientRect();
    return vertical ? e.clientY > rect.top + rect.height / 2 : e.clientX > rect.left + rect.width / 2;
}

// Move an item within an array to a target insertion index (0..len), accounting for the removal shift.
export function moveInArray<T>(arr: T[], from: number, insertAt: number): T[] {
    const next = [...arr];
    const [moved] = next.splice(from, 1);
    next.splice(from < insertAt ? insertAt - 1 : insertAt, 0, moved);
    return next;
}

// AC-964 / S6.2 — element DnD spans columns, so the shared drag descriptor + move handlers live on a
// context the parent editor provides and each column cell consumes. Absent context = buttons-only.
export interface IElementDropTarget { row: number; col: number; index: number; after: boolean }

export interface ILayoutDndContext {
    drag: IDragPayload | null;
    over: IElementDropTarget | null;
    startElementDrag: (e: React.DragEvent, loc: { row: number; col: number; el: number }) => void;
    setOverElement: (v: IElementDropTarget | null) => void;
    dropElement: (target: { row: number; col: number; insertAt: number }) => void;
    // AC-965 / S6.3 — drop a new element (dragged from the palette) at a position.
    addElementAt: (target: { row: number; col: number; insertAt: number }, elementType: string) => void;
    endDrag: () => void;
}

export const LayoutDndContext = React.createContext<ILayoutDndContext | null>(null);
